<template>
  <b-overlay
    :show="show"
    :variant="variant"
    :opacity="opacity"
    :blur="blur"
    rounded="lg"
    spinner-variant="primary"
  >
    <div
      class="h-screen flex w-full bg-img vx-row no-gutter items-center justify-center"
      id="page-login"
      v-bind:style="styleObject"
    >
      <div class="vx-col sm:w-2/3 md:w-2/3 lg:w-2/3 xl:w-2/3 sm:m-10 m-10">
        <div class="mb-5" id="logo_image">
          <img :src="get_logo" :width="'200px'" />
        </div>
        <vx-card class="mt-5">
          <form>
            <div slot="no-body" class="full-page-bg-color">
              <div class="vx-row no-gutter justify-center items-center">
                <div
                  class="vx-col sm:w-full md:w-full lg:w-full d-theme-dark-bg"
                >
                  <div class="p-8 login-tabs-container">
                    <div class="vx-card__title mb-4">
                      <h4 class="mb-4">{{ $t("SignUp") }}</h4>
                    </div>
                    <div v-if="!showSuccess">
                      <div class="vx-row">
                        <!-- required -->
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-2"
                        >
                          <label class="vs-input--label">{{
                            $t("FirstName")
                          }}</label>
                          <b-input
                            size="lg"
                            v-validate="'required'"
                            :data-vv-as="$t('FirstName')"
                            name="first_name"
                            v-model="first_name"
                            class="w-full"
                            @keydown.enter="checkForm"
                            :placeholder="$t('FirstName')"
                          />
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('first_name')"
                            >{{ errors.first("first_name") }}</span
                          >
                        </div>
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-2"
                        >
                          <label class="vs-input--label">{{
                            $t("LastName")
                          }}</label>
                          <b-input
                            size="lg"
                            v-validate="'required'"
                            name="last_name"
                            :data-vv-as="$t('LastName')"
                            v-model="last_name"
                            class="w-full"
                            @keydown.enter="checkForm"
                            :placeholder="$t('LastName')"
                          />
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('last_name')"
                            >{{ errors.first("last_name") }}</span
                          >
                        </div>
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
                        >
                          <label class="vs-input--label">{{
                            $t("CompanyName")
                          }}</label>
                          <b-input
                            size="lg"
                            v-validate="'required|max:100'"
                            name="company_name"
                            icon-no-border
                            icon="icon icon-users"
                            icon-pack="feather"
                            v-model="company_name"
                            class="w-full"
                            @keydown.enter="submit"
                            :data-vv-as="$t('CompanyName')"
                            :placeholder="$t('CompanyName')"
                          />
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('company_name')"
                            >{{ errors.first("company_name") }}</span
                          >
                        </div>
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2"
                          id="contact_label"
                        >
                          <label class="vs-input--label">{{
                            $t("ContactNumber")
                          }}</label>
                          <vue-tel-input
                            size="lg"
                            v-validate="{
                              required: true,
                              regex: /^(?=.*[0-9])[- +()0-9]+$/,
                              min: 12,
                            }"
                            v-model="contact_number"
                            v-on:country-changed="countryChanged"
                            v-bind="bindProps"
                            v-on:blur="appendCode"
                            @onInput="onInput"
                            @keydown.enter="submit"
                            class="w-full contact_number"
                            :placeholder="$t('ContactNumber')"
                            :label="$t('ContactNumber')"
                            :data-vv-as="$t('ContactNumber')"
                          ></vue-tel-input>

                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('contact_number')"
                            >{{ errors.first("contact_number") }}</span
                          >
                        </div>
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
                        >
                          <label class="vs-input--label">{{
                            $t("Email")
                          }}</label>
                          <b-input
                            size="lg"
                            v-validate="'required|email'"
                            name="email"
                            icon-no-border
                            icon="icon icon-user"
                            icon-pack="feather"
                            class="w-full"
                            v-model="email"
                            @keydown.enter="submit"
                            :data-vv-as="$t('Email')"
                            :placeholder="$t('Email')"
                          />
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('email')"
                            >{{ errors.first("email") }}</span
                          >
                        </div>
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
                        >
                          <label class="vs-input--label">{{
                            $t("Password")
                          }}</label>
                          <b-input
                            size="lg"
                            v-validate="'required|min:8|numbers|upCase|loCase'"
                            type="password"
                            name="password"
                            icon-no-border
                            icon="icon icon-lock"
                            icon-pack="feather"
                            class="w-full"
                            v-model="password"
                            @input="displayError"
                            ref="password"
                            :data-vv-as="$t('Password')"
                            @keydown.enter="submit"
                            :placeholder="$t('Password')"
                          />
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('password')"
                            >{{ errors.first("password") }}</span
                          >
                        </div>
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
                        >
                          <label class="vs-input--label">{{
                            $t("ConfirmPassword")
                          }}</label>
                          <b-input
                            size="lg"
                            v-validate="'required|min:8|confirmed:password'"
                            type="password"
                            name="confirm_password"
                            icon-no-border
                            icon="icon icon-lock"
                            icon-pack="feather"
                            class="w-full"
                            v-model="confirm_password"
                            @keydown.enter="submit"
                            :data-vv-as="$t('ConfirmPassword')"
                            :placeholder="$t('ConfirmPassword')"
                          />
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('confirm_password')"
                            >{{ errors.first("confirm_password") }}</span
                          >
                        </div>

                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
                        >
                          <label class="vs-input--label">{{
                            $t("SelectWeekDays")
                          }}</label>
                          <v-select
                            v-validate="'required'"
                            multiple
                            v-model="opening_days"
                            :options="weekdays"
                            name="opening_days"
                            :placeholder="$t('SelectWeekDays')"
                            class="w-full"
                            :data-vv-as="$t('Days')"
                          >
                            <template v-slot:option="option">
                              <span :class="option.icon"
                                >{{ option.label }}
                              </span>
                              <feather-icon
                                v-if="opening_days.includes(option.label)"
                                icon="CheckIcon"
                                class="float-right"
                                svgClasses="w-5 h-5"
                              />
                            </template>
                          </v-select>
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('opening_days')"
                            >{{ errors.first("opening_days") }}</span
                          >
                        </div>

                        <div
                          class="vx-col mt-5 w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2"
                        >
                          <label class="vs-input--label">{{
                            $t("OpeningTime")
                          }}</label>
                          <!-- <b-form-timepicker
                            :config="configdateTimePicker"
                            v-model="start_time"
                            :placeholder="$t('OpeningTime')"
                            class="flatpick w-full"
                            v-validate="'required'"
                            :data-vv-as="$t('OpeningTime')"
                            name="start_time"
                            @input="validateStartDate()"
                            locale="en"
                          /> -->
                                <flat-pickr
                       :config="configdateTimePicker"
                            v-model="start_time"
                            :placeholder="$t('OpeningTime')"
                         class="flatpickr-input w-full p-3"
                            v-validate="'required'"
                            :data-vv-as="$t('OpeningTime')"
                            name="start_time"
                            @input="validateStartDate()"
                            locale="en"
                      />
                      <br />
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('start_time')"
                            >{{ errors.first("start_time") }}</span
                          >
                          <span :class="'text-danger startdate-error--'"></span>
                        </div>

                        <div
                          class="vx-col mt-5 w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2"
                        >
                          <label class="vs-input--label">{{
                            $t("ClosingTime")
                          }}</label>

                          <!-- <b-form-timepicker
                            :config="configdateTimePicker"
                            v-model="end_time"
                            :placeholder="$t('ClosingTime')"
                            class="flatpick w-full"
                            v-validate="'required'"
                            :data-vv-as="$t('ClosingTime')"
                            name="end_time"
                            @input="validateEndDate"
                            :locale="$store.getters.locale"
                          /> -->
                                  <flat-pickr
                      :config="configdateTimePicker"
                            v-model="end_time"
                            :placeholder="$t('ClosingTime')"
                      class="flatpickr-input w-full p-3"
                            v-validate="'required'"
                            :data-vv-as="$t('ClosingTime')"
                            name="end_time"
                            @input="validateEndDate"
                            :locale="$store.getters.locale"
                      />
                      <br />

                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('end_time')"
                            >{{ errors.first("end_time") }}</span
                          >
                          <span :class="'text-danger enddate-error--'"></span>
                        </div>

                        <!-- <div class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5">
                            <label class="vs-input--label">{{$t('Plan')}}</label>
                            <v-select
                              v-validate="'required'"
                              v-model="plan"
                              :options="planOptions"
                              :reduce="name => name.slug"
                              label="name"
                              :placeholder="$t('SelectPlan')"
                              :data-vv-as="$t('Plan')"
                              name="plan"
                            />
                            <span
                              class="text-danger text-sm"
                              v-show="errors.has('plan')"
                            >{{ errors.first('plan') }}</span>
                      </div>-->
                        <div
                          class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
                        >
                          <label class="vs-input--label">{{
                            $t("Language")
                          }}</label>
                          <v-select
                            v-validate="'required'"
                            v-model="language"
                            :options="languages"
                            :reduce="(label) => label.value"
                            label="label"
                            :placeholder="$t('SelectLanguage')"
                            :data-vv-as="$t('Language')"
                            name="language"
                          >
                            <template v-slot:option="option">
                              <span :class="option.icon"
                                >{{ option.label }}
                              </span>
                              <feather-icon
                                v-if="language && language == option.value"
                                icon="CheckIcon"
                                class="float-right"
                                svgClasses="w-5 h-5"
                              />
                            </template>
                          </v-select>
                          <span
                            class="text-danger text-sm"
                            v-show="errors.has('language')"
                            >{{ errors.first("language") }}</span
                          >
                        </div>
                      </div>

                      <div class="vx-col w-full message">
                        <label class="vs-input--label">{{
                          $t("WelcomeMessage")
                        }}</label>
                        <b-textarea
                          v-validate="'required'"
                          v-model="welcome_message"
                          rows="3"
                          :placeholder="$t('WelcomeMessage')"
                          :label="$t('WelcomeMessage')"
                          :data-vv-as="$t('WelcomeMessage')"
                          name="welcome_message"
                          @keyup.enter.exact="checkForm"
                        />
                        <span
                          class="text-danger text-sm"
                          v-show="errors.has('welcome_message')"
                          >{{ errors.first("welcome_message") }}</span
                        >
                      </div>
                      <br />

                      <b-button
                        variant="primary"
                        class="float-left"
                        @click="back"
                        >{{ $t("Back") }}</b-button
                      >
                      <b-button
                        variant="primary"
                        class="float-right"
                        @click="submit"
                        >{{ $t("Submit") }}</b-button
                      >
                    </div>
                    <div v-if="showSuccess">
                      <div style="margin-bottom: 37px">
                        <br />
                        <h5>{{ $t("ThankYou") }}</h5>
                        <p>{{ $t("ThankYouText") }}</p>
                      </div>
                    </div>
                    <br />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </vx-card>
      </div>
    </div>
  </b-overlay>
</template>


<script>
// For custom error message
import { BFormTimepicker } from "bootstrap-vue";
import { Validator } from "vee-validate";
import { phoneConfig } from "@/utils/helper";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";
import "moment-timezone";
import $ from "jquery";
import { getLanguage } from "@/utils/helper";
export default {
  data() {
    return {
      email: "",
      first_name: "",
      last_name: "",
      contact_number: "",
      company_name: "",
      password: "",
      confirm_password: "",
      welcome_message: "",
      opening_days: [],
      start_time: "",
      end_time: "",
      language: "",
      plan_slug: "trial",
      styleObject: {
        height: "170vh !important",
        background:
          "url(" + this.$store.getters.whitelabel.background_image.path + ")",
      },

      showSuccess: false,
      logo: require("@/assets/images/logo/logo.png"),
      follow: false,
      country: null,
      bindProps: phoneConfig(this.$t("ContactNumber")),
      weekdays: this.$t("weekDays"),
      configdateTimePicker: {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
      },
      start_time_error: false,
      end_time_error: false,
      // plans: [],
      // plan: "Standard",
      // planOptions: [],
      languages: getLanguage(this.$t("lang")),
      timeZone: "",
      show: false,
      variant: "light",
      opacity: 0.85,
      blur: "2px",
      error: "",
      success: "",
    };
  },
  components: {
    "v-select": vSelect,
    flatPickr,
    BFormTimepicker,
  },
  watch: {
    background_image: function (newValue) {
      this.styleObject.background = "url(" + newValue + ")";
    },
    get_logo: function (newValue) {},
    "$i18n.locale": {
      handler: function (value) {
        this.loadLocale(value);
        this.displayError();
        this.languages = getLanguage(this.$t("lang"));
      },
    },
  },
  computed: {
    get_logo() {
      return this.$store.getters.whitelabel.logo.path;
    },
    background_image() {
      return this.$store.getters.whitelabel.background_image.path;
    },
  },
  created() {
    this.$validator.extend("upCase", {
      getMessage: () => this.$t("errorMessage"),
      validate: (value) => value.match(/[A-Z]/g) !== null,
    });
    this.$validator.extend("numbers", {
      getMessage: () => this.$t("errorMessage"),
      validate: (value) => value.match(/[0-9]/g) !== null,
    });
    this.$validator.extend("loCase", {
      getMessage: () => this.$t("errorMessage"),
      validate: (value) => value.match(/[a-z]/g) !== null,
    });
    //  this.getPlan();
    this.timeZone =
      moment.tz.guess() !== "Asia/Calcutta"
        ? moment.tz.guess()
        : "Asia/Kolkata";
    this.loadLocale(this.$i18n.locale);
  },
  methods: {
    //display error
    displayError() {
      this.$validator.extend("upCase", {
        getMessage: () => this.$t("errorMessage"),
        validate: (value) => value.match(/[A-Z]/g) !== null,
      });
      this.$validator.extend("numbers", {
        getMessage: () => this.$t("errorMessage"),
        validate: (value) => value.match(/[0-9]/g) !== null,
      });
      this.$validator.extend("loCase", {
        getMessage: () => this.$t("errorMessage"),
        validate: (value) => value.match(/[a-z]/g) !== null,
      });
    },
    errorClass(rule) {
      return {
        error: this.errors.firstByRule("password", rule),
      };
    },
    /**
     * redirect back
     */
    back() {
      this.$router.push("/company/login");
    },
    /**
     * validate phone number
     */
    onInput({ number, isValid, country }) {
      this.contact_number = number["international"];
    },
    /**
     * vee validate with multi lang
     */
    loadLocale(code) {
      return import("vee-validate/dist/locale/" + code).then((locale) => {
        Validator.localize(code, locale);
      });
    },
    /**
     * append country code
     */
    appendCode() {
      if (this.contact_number.includes("+") == false) {
        this.contact_number = this.country + " " + this.contact_number;
      }
    },
    /**
     * Country Code
     */
    countryChanged(country) {
      this.country = "+" + country.dialCode;
    },
    /**
     * form submit
     */
    submit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.show = true;
          var data = {
            first_name: this.first_name,
            last_name: this.last_name,
            company_name: this.company_name,
            contact_number: this.contact_number,
            email: this.email,
            password: this.password,
            start_time: this.start_time,
            end_time: this.end_time,
            welcome_message: this.welcome_message,
            opening_days: this.opening_days,
            plan_slug: this.plan_slug,
            language: this.language,
            timezone: this.timeZone,
            is_unio: 0,
            is_postpaid: 0,
            is_keypad: 0,
          };

          this.$store
            .dispatch("CompanySignup", data)
            .then(() => {
              this.show = false;
              this.showSuccess = true;
              if (this.showSuccess) {
                this.styleObject.height = "100vh !important";
              }
            })
            .catch((error) => {
              this.$log.error(
                "File : Signup.vue | Function : submit() | Error :",
                error
              );
              this.show = false;
              // Error response
              this.error =
                this.$t(error.replace(/[\s\/]/g, "")) !==
                error.replace(/[\s\/]/g, "")
                  ? this.$t(error.replace(/[\s\/]/g, ""))
                  : error;
              this.$toasted.show(this.error, {
                className: "top-alert",
                position: "top-center",
                type: "error",
                duration: 2000,
              });
              return false;
            });
        } else {
          return false;
        }
      });
    },
    validateStartDate() {
      this.start_time_error = false;
      this.end_time_error = false;
      var start_time = moment(this.start_time, "HH:mm:ss");
      var end_time = moment(this.end_time, "HH:mm:ss");

      if (start_time >= end_time) {
        // checked if same as selcetd date range
        $(".startdate-error--").html(this.$t("EndTimeLessError"));
        this.start_time_error = true;
      } else if (start_time <= end_time) {
        this.start_time_error = false;
        $(".startdate-error--").html("");
      }
    },
    /**
     * End time validtaion
     */
    validateEndDate() {
      this.start_time_error = false;
      this.end_time_error = false;
      var start_time = moment(this.start_time, "HH:mm:ss");
      var end_time = moment(this.end_time, "HH:mm:ss");

      if (end_time <= start_time) {
        // check if greater than end date
        $(".enddate-error--").html(this.$t("EndTimeLessError"));
        this.end_time_error = true;
      } else if (end_time >= start_time) {
        this.end_time_error = false;
        $(".enddate-error--").html("");
        $(".startdate-error--").html("");
      }
    },
  },
};
</script>

<style scoped>
.vs-input--label {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
}
#update {
  display: inline-flex;
}

#cancel {
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  margin-left: 20px;
  margin-top: 20px;
}

.message {
  margin-top: 20px;
}

.switch {
  margin-top: 20px;
}

.contact_number {
  padding: 8px !important;
}

#contact_label {
  margin-top: 15px;
}
.bg-img {
  background-repeat: repeat !important;
}
.flatpick {
  padding: 5px;
}
</style>